import {DataBaseComponent, DataBaseComponentState} from "../../components/DataBaseComponent";
import SaveDialogLayout from "../../components/DialogLayout/SaveDialogLayout";
import {Select, Text} from "../../components/form";
import repository from "../../client/repository";
import * as React from "react";
import ReefMultiSelect from "shared/ReefMultiSelect";
import {ReefResource} from "client/resources/reefResource";
import {uniq} from "lodash";
import * as semver from "semver";

interface Props {
    reefs: ReefResource[];
    onChange: (taskId?: string) => void;
}

interface State extends DataBaseComponentState {
    fromVersion: string;
    versionToChange: string;
    excludedReefIds: string[];
}

class BulkChangeHostedScriptVersionDialog extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            fromVersion: "",
            versionToChange: "",
            excludedReefIds: []
        };
    }

    render() {
        const downgradeError = this.isDowngrade()
            ? `Bulk downgrades are not supported: ${this.state.versionToChange} is lower than ${this.state.fromVersion}`
            : undefined;
        return <SaveDialogLayout
            title="Hosted Scripts Version" saveButtonLabel="Update"
            onSaveClick={() => this.bulkChangeHostedScriptVersion()}
            errors={this.state.errors}
            saveButtonDisabled={!this.state.versionToChange || !this.state.fromVersion || !semver.valid(this.state.versionToChange) || !!downgradeError}
            busy={this.state.busy}>
            <Select value={this.state.fromVersion}
                onChange={(value: string) => this.setState({fromVersion: value})}
                items={this.getCurrentVersions().map(v => ({text: v, value: v}))}
                fieldName="Current version"
                hintText="Only reefs on this hosted scripts version will be upgraded"
            />
            <Text label="New version"
                value={this.state.versionToChange}
                onChange={value => this.setState({versionToChange: value})}
                validate={value => value && !semver.valid(value) ? "Enter a valid semantic version, e.g. 18.3055.0" : ""}
                error={downgradeError}
            />
            <ReefMultiSelect
                label="Reefs to exclude from the upgrade"
                items={this.props.reefs}
                value={this.state.excludedReefIds}
                onChange={excludedReefIds => this.setState({excludedReefIds})}
            />
        </SaveDialogLayout>;
    }

    private isDowngrade(): boolean {
        const from = semver.coerce(this.state.fromVersion);
        const to = semver.coerce(this.state.versionToChange);
        return !!from && !!to && semver.compare(to, from) < 0;
    }

    private getCurrentVersions(): string[] {
        const versions: string[] = [];
        this.props.reefs.forEach(r => r.KubernetesClusters.forEach(k => versions.push(k.HostedScriptsVersion)));
        return uniq(versions).sort((a, b) => {
            const semverA = semver.coerce(a);
            const semverB = semver.coerce(b);
            return semverA && semverB ? semver.compare(semverA, semverB) : a.localeCompare(b);
        });
    }

    private async bulkChangeHostedScriptVersion(): Promise<boolean> {
        return await this.doBusyTask(async () => {
            const taskId = await repository.Reefs.bulkUpdateHostedScriptVersions({
                Version: this.state.versionToChange,
                FromVersion: this.state.fromVersion,
                ExcludedReefIds: this.state.excludedReefIds
            });
            this.props.onChange(taskId);
        });
    }
}

export default BulkChangeHostedScriptVersionDialog;
